import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { marketplaceApi } from '../../api/marketplace';
import PackOpeningAnimation from '../PackOpeningAnimation';
import MultiPackOpening from './MultiPackOpening';
import { useLanguage } from '../../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../../context/ConfirmD10SContext';

const PackCard = ({ pack, user, onOpenSuccess, onError }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { confirm } = useConfirmSpendD10S();
  const [isOpening, setIsOpening] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [openedCards, setOpenedCards] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [multiPackResult, setMultiPackResult] = useState(null);
  const packPrice = parseFloat(pack.price) || 0;
  const totalPrice = packPrice * quantity;
  const safeQty = Math.max(1, Math.min(100, quantity));

  const handleOpenPack = async () => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToOpenPack'));
      return;
    }

    const ok = await confirm(totalPrice, t('marketplaceCards.openPack'));
    if (!ok) return;

    setIsOpening(true);
    setMultiPackResult(null);
    try {
      const response = await marketplaceApi.openPack(pack.id, {
        userId: user.id,
        quantity: safeQty
      });

      const opening = response?.packOpening;
      if (!opening) {
        onOpenSuccess();
        return;
      }

      // Multiple packs: show sequential opening with skip
      if (opening.packOpenings && Array.isArray(opening.packOpenings) && opening.packOpenings.length > 0) {
        setMultiPackResult({
          packOpenings: opening.packOpenings,
          totalCards: opening.totalCards || opening.packOpenings.reduce((s, p) => s + (p.count || p.cards?.length || 0), 0),
          totalPacks: opening.totalPacks || opening.packOpenings.length,
          packName: opening.packName || pack.name
        });
        setShowAnimation(false);
      } else if (opening.cards && opening.cards.length > 0) {
        setOpenedCards(opening.cards);
        setShowAnimation(true);
      } else {
        onOpenSuccess();
      }
    } catch (error) {
      onError(error.message || t('marketplaceCards.openPackFailed'));
    } finally {
      setIsOpening(false);
    }
  };

  const handleAnimationFinish = () => {
    setShowAnimation(false);
    setOpenedCards(null);
    onOpenSuccess();
  };

  const handleAnimationClose = () => {
    setShowAnimation(false);
    setOpenedCards(null);
    onOpenSuccess();
  };

  const handleMultiPackClose = () => {
    setMultiPackResult(null);
    onOpenSuccess();
  };

  const handleViewInProfile = () => {
    setMultiPackResult(null);
    onOpenSuccess();
    navigate('/profile');
  };

  return (
    <>
      <div className="football-card glass-football p-6 card-hover">
        <div className="text-center mb-4">
          <Package className="w-16 h-16 text-[#4169E1] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#1e293b] mb-2">{pack.name}</h3>
          <p className="text-[#475569] text-sm mb-4 leading-relaxed">
            {pack.description || t('marketplaceCards.packDescriptionFallback')}
          </p>
          <div className="border-t border-[#E2E8F0] pt-3 mt-3 space-y-3">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
                className="w-24 min-w-[5rem] px-3 py-2 rounded-lg border border-[#cbd5e1] text-base text-center text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#4169E1]"
                aria-label={t('marketplaceCards.packQuantityLabel')}
              />
              <span className="text-[#475569] text-sm font-medium">
                {t('marketplaceCards.totalCost')}: <span className="text-[#4169E1] font-bold">{totalPrice.toFixed(2)} DIOS</span>
                {' · '}
                {safeQty === 1
                  ? t('marketplaceCards.openingOnePack')
                  : t('marketplaceCards.openingPacksCount').replace('{count}', safeQty)}
              </span>
            </div>
          </div>
        </div>
        <button
          className="btn-football w-full"
          onClick={handleOpenPack}
          disabled={isOpening || !user?.id}
        >
          {isOpening ? t('marketplaceCards.openingPack') : `📦 ${t('marketplaceCards.openPack')}`}
        </button>
      </div>

      {showAnimation && openedCards && (
        <PackOpeningAnimation
          cards={openedCards}
          packName={pack.name}
          onClose={handleAnimationClose}
          onFinish={handleAnimationFinish}
          user={user}
        />
      )}

      {multiPackResult && (
        <MultiPackOpening
          packOpenings={multiPackResult.packOpenings}
          totalCards={multiPackResult.totalCards}
          totalPacks={multiPackResult.totalPacks}
          packName={multiPackResult.packName}
          user={user}
          onClose={handleMultiPackClose}
          onViewInProfile={handleViewInProfile}
        />
      )}
    </>
  );
};

export default PackCard;
